'use client'
import { yupResolver } from '@hookform/resolvers/yup'
import cn from 'classnames'
import { forwardRef, useCallback } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { toast } from 'react-toastify'

import { Icon } from '@/icons'

import { AcceptCheckbox } from '@/components/AcceptCheckbox'
import { Button } from '@/components/Button'
import { FormInput } from '@/components/FormInput'
import { HeaderTag } from '@/components/HeaderTag'
import { Loader } from '@/components/Loader'

import { YANDEX_METRIKA, YANDEX_METRIKA_COUNTER } from '@/config'

import { getHelp } from '@/app/actions/help/actions'
import { marketingStorage } from '@/store/MarketingStorage'
import { HelpTypeSite } from '@/services/Api/generated'

import { object as yupObject } from 'yup'

import { acceptTermsValidator, emailValidator } from '@/utils/validator'

import { useCaptcha } from '@/hooks/Captcha'

import { maxPopupStore, maxSubscribeTexts } from '@/store/MaxPopup.store'
import styles from './Subscription.module.scss'

interface IFormData {
  email: string
  accept: boolean
}

const schema = yupObject().shape({
  email: emailValidator,
  accept: acceptTermsValidator,
})

type SubscriptionProps = {
  className?: string
}
export const Subscription = forwardRef<HTMLDivElement, SubscriptionProps>(
  ({ className }, ref) => {
    const { getToken } = useCaptcha()

    const {
      register,
      setValue,
      setError,
      handleSubmit,
      formState: { isSubmitting, errors },
    } = useForm<IFormData>({
      mode: 'onChange',
      resolver: yupResolver(schema),
    })

    const onSubmit: SubmitHandler<IFormData> = useCallback(
      async (data) => {
        const tokenCaptcha = await getToken()
        const response = await getHelp(
          {
            type: HelpTypeSite.AskNewsSubscribe,
            data: {
              sourceUrl: window.location.href.split('?')[0],
              email: data.email,
              utmClientId: marketingStorage.getClientId() ?? undefined,
            },
          },
          tokenCaptcha
        )
        if (response.status) {
          marketingStorage.clearClientId()
          if (YANDEX_METRIKA) {
            // @ts-ignore
            ym(YANDEX_METRIKA_COUNTER, 'reachGoal', 'FORM_SUBSCRIPTION_SUBMIT')
          }
          setValue('email', '')
          setValue('accept', false)
          maxPopupStore.show({
            sourceSite: response.data.params,
            ...maxSubscribeTexts,
          })
        } else {
          if (response.errors?.['data.email'] != null) {
            setError('email', {
              type: 'custom',
              message: 'Пожалуйста, введите корректный e-mail',
            })
          } else {
            toast.error('Что то пошло не так! Попробуйте позже')
          }
        }
      },
      [setValue, setError, getToken]
    )
    return (
      <div className={cn(styles.container, className)} ref={ref}>
        <Icon name="mail" className={styles.iconMail} />
        <HeaderTag
          icon="inbox"
          title="Никакого спама"
          className={styles.noSpam}
        />
        <div className={styles.title}>Подписка на новости и статьи</div>
        <div className={styles.description}>
          Рассказываем, как заработать на интеллектуальной собственности, идеях
          и технологиях
        </div>
        <form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
          <div className={styles.formRow}>
            <FormInput
              className={styles.input}
              placeholder="Email"
              error={errors.email?.message}
              {...register('email')}
            />
            <Button
              type="submit"
              className={styles.subscribeButton}
              disabled={isSubmitting}
              compactHover
            >
              Подписаться
              {isSubmitting ? <Loader onTop /> : null}
            </Button>
          </div>
          <AcceptCheckbox<IFormData>
            register={register}
            error={errors.accept?.message}
          />
        </form>
        <div className={styles.overflow}>
          <img src="/images/dots.svg" className={styles.dots} alt="" />
        </div>
      </div>
    )
  }
)
